import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Bot, MessageSquare, Truck, Send, ShoppingBag, Eraser } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";

type DemoStatus = {
  aiAgentsEnabled: boolean;
  demoModeEnabled: boolean;
  demoSellers: {
    id: string;
    displayName: string;
    email: string | null;
    orders: {
      orderId: string;
      orderStatus: string | null;
      createdAt: string | null;
      buyerId: string;
      buyerName: string;
      lastMessage: { senderId: string; body: string; createdAt: string | null } | null;
      unreadFromBuyer: number;
    }[];
  }[];
  demoBuyers: { id: string; displayName: string; email: string | null }[];
  demoDrivers: {
    id: string;
    displayName: string;
    email: string | null;
    homeLat: string | null;
    homeLng: string | null;
  }[];
  openDeliveries: {
    id: string;
    orderId: string;
    buyerCity: string;
    buyerState: string;
    driverPay: string | number;
    deliverByDate: string | null;
    stops: number;
  }[];
  betaListings: {
    id: string;
    name: string;
    sellerId: string;
    sellerDisplayName: string | null;
    price: string;
    qtyAvailable: number;
  }[];
};

export default function AdminDemoControl() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<DemoStatus>({
    queryKey: ["/api/admin/demo-control/status"],
    refetchInterval: 15_000,
  });

  const aiToggle = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiRequest("POST", "/api/admin/demo-control/ai-toggle", { enabled });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/demo-control/status"] });
      toast({ title: "AI agents toggled" });
    },
    onError: (err: Error) => toast({ title: "Failed", description: err.message, variant: "destructive" }),
  });

  const seedDrivers = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/demo-control/seed-drivers");
      return res.json() as Promise<{ created: number; updated: number; total: number }>;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/demo-control/status"] });
      toast({ title: `Seeded demo drivers (${result.total} total)` });
    },
    onError: (err: Error) => toast({ title: "Seed failed", description: err.message, variant: "destructive" }),
  });

  const resetHistory = useMutation({
    mutationFn: async (emails: string[]) => {
      const res = await apiRequest("POST", "/api/admin/demo-control/reset-account-history", { emails });
      return res.json() as Promise<{
        summary: Array<{
          email: string;
          userId: string | null;
          ordersDeleted: number;
          notificationsDeleted: number;
          savedItemsDeleted: number;
          followsDeleted: number;
          missionsDeleted: number;
          deliveryDeclinesDeleted: number;
          deliveriesReleased: number;
        }>;
      }>;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      const lines = result.summary.map((row) => {
        if (!row.userId) return `${row.email}: not found`;
        return `${row.email}: ${row.ordersDeleted} orders, ${row.notificationsDeleted} notifs cleared`;
      });
      toast({ title: "History reset", description: lines.join(" · ") });
    },
    onError: (err: Error) => toast({ title: "Reset failed", description: err.message, variant: "destructive" }),
  });

  const demoModeToggle = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiRequest("POST", "/api/admin/demo-control/demo-mode-toggle", { enabled });
      return res.json() as Promise<{ demoModeEnabled: boolean }>;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/demo-control/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/public/config"] });
      queryClient.invalidateQueries({ queryKey: ["/api/items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      toast({ title: result.demoModeEnabled ? "Demo mode on" : "Demo mode off — app in live view" });
    },
    onError: (err: Error) => toast({ title: "Toggle failed", description: err.message, variant: "destructive" }),
  });

  if (!user || !(user as any).isDemoAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto max-w-3xl px-4 py-8">
          <p>Admin access required.</p>
          <Button className="mt-2" onClick={() => setLocation("/")}>
            Back home
          </Button>
        </main>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto max-w-3xl px-4 py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto max-w-3xl px-4 py-6 pb-24 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Demo Control</h1>
          <p className="text-sm text-muted-foreground">
            Run beta-tester sessions manually when AI is off, or oversee the simulation when it's on.
          </p>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Demo mode
              </CardTitle>
              <CardDescription>
                When off: demo sellers, demo orders, and the countdown banner are hidden;
                feedback button is admin-only; AI agents stop. Demo data is preserved —
                flip back on any time to restore.
              </CardDescription>
            </div>
            <Switch
              checked={data.demoModeEnabled}
              onCheckedChange={(checked) => demoModeToggle.mutate(checked)}
              disabled={demoModeToggle.isPending}
            />
          </CardHeader>
        </Card>

        <Card className={data.demoModeEnabled ? undefined : "opacity-50"}>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                AI demo agents
              </CardTitle>
              <CardDescription>
                When off, you puppet the demo sellers and driver yourself from this page.
                {!data.demoModeEnabled && " (Disabled while Demo mode is off.)"}
              </CardDescription>
            </div>
            <Switch
              checked={data.aiAgentsEnabled}
              onCheckedChange={(checked) => aiToggle.mutate(checked)}
              disabled={aiToggle.isPending || !data.demoModeEnabled}
            />
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Demo seller inbox
            </CardTitle>
            <CardDescription>
              Reply to any demo seller's order chats as that seller. Messages post under the demo seller's identity.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.demoSellers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No demo sellers seeded yet.</p>
            ) : (
              data.demoSellers.map((seller) => (
                <SellerInbox key={seller.id} seller={seller} onSent={() => {
                  queryClient.invalidateQueries({ queryKey: ["/api/admin/demo-control/status"] });
                }} />
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Buy as demo buyer
            </CardTitle>
            <CardDescription>
              Place a paid order on a beta tester's listing as a demo buyer. Skips Stripe; opens a community delivery request the AI driver (or you) can accept.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.betaListings.length === 0 ? (
              <p className="text-sm text-muted-foreground">No published listings from beta testers yet.</p>
            ) : (
              data.betaListings.map((listing) => (
                <BuyAsDemoBuyer
                  key={listing.id}
                  listing={listing}
                  buyers={data.demoBuyers}
                  onPlaced={() => queryClient.invalidateQueries({ queryKey: ["/api/admin/demo-control/status"] })}
                />
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Demo driver controls
            </CardTitle>
            <CardDescription>
              Pick a demo driver to accept any open delivery. Schedule it within 7 days of the order date.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.demoDrivers.length === 0 && (
              <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800/40 dark:bg-amber-950/30">
                <p>
                  No demo drivers seeded. Without them, community-delivery
                  orders sit at "Waiting for driver" forever.
                </p>
                <Button
                  size="sm"
                  onClick={() => seedDrivers.mutate()}
                  disabled={seedDrivers.isPending}
                >
                  {seedDrivers.isPending ? "Seeding…" : "Seed demo drivers"}
                </Button>
              </div>
            )}
            {data.openDeliveries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No open delivery requests.</p>
            ) : (
              data.openDeliveries.map((req) => (
                <AcceptAsDriver
                  key={req.id}
                  request={req}
                  drivers={data.demoDrivers}
                  onAccepted={() => {
                    queryClient.invalidateQueries({ queryKey: ["/api/admin/demo-control/status"] });
                  }}
                />
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eraser className="h-5 w-5" />
              Reset account history
            </CardTitle>
            <CardDescription>
              Wipes orders, messages, notifications, deliveries, reviews, saved
              items, follows, and mission progress for the listed accounts.
              Profiles, listings, fulfillment profiles, and addresses are kept.
              Deliveries you accepted on other people's orders are released back
              to the open board. Cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              size="sm"
              disabled={resetHistory.isPending}
              onClick={() => {
                const emails = ["markalty12@gmail.com", "testing123@gmail.com"];
                if (window.confirm(`Wipe history for ${emails.join(" + ")}? This cannot be undone.`)) {
                  resetHistory.mutate(emails);
                }
              }}
            >
              {resetHistory.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Reset markalty12 + testing123
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function SellerInbox({
  seller,
  onSent,
}: {
  seller: DemoStatus["demoSellers"][number];
  onSent: () => void;
}) {
  const { toast } = useToast();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const send = useMutation({
    mutationFn: async ({ orderId, message }: { orderId: string; message: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/demo-control/orders/${orderId}/reply-as-seller`,
        { sellerId: seller.id, message },
      );
      return res.json();
    },
    onSuccess: (_data, vars) => {
      setDrafts((d) => ({ ...d, [vars.orderId]: "" }));
      toast({ title: "Sent as " + seller.displayName });
      onSent();
    },
    onError: (err: Error) => toast({ title: "Failed", description: err.message, variant: "destructive" }),
  });

  return (
    <div className="rounded-md border p-3">
      <div className="font-medium">{seller.displayName}</div>
      <p className="text-xs text-muted-foreground">{seller.email}</p>
      {seller.orders.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No orders yet.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {seller.orders.map((order) => (
            <div key={order.orderId} className="rounded border bg-muted/40 p-2 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-1">
                <span className="font-medium">
                  Order #{order.orderId.slice(0, 8).toUpperCase()} — {order.buyerName}
                </span>
                <div className="flex gap-1">
                  <Badge variant="outline" className="text-[10px]">{order.orderStatus ?? "pending"}</Badge>
                  {order.unreadFromBuyer > 0 && (
                    <Badge variant="destructive" className="text-[10px]">
                      {order.unreadFromBuyer} unread
                    </Badge>
                  )}
                </div>
              </div>
              {order.lastMessage && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Last: <em>{order.lastMessage.body.slice(0, 120)}</em>
                </p>
              )}
              <div className="mt-2 flex gap-2">
                <Textarea
                  rows={2}
                  placeholder="Reply as this demo seller…"
                  value={drafts[order.orderId] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [order.orderId]: e.target.value }))}
                  className="text-sm"
                />
                <Button
                  size="sm"
                  disabled={!(drafts[order.orderId] ?? "").trim() || send.isPending}
                  onClick={() => {
                    const msg = (drafts[order.orderId] ?? "").trim();
                    if (msg) send.mutate({ orderId: order.orderId, message: msg });
                  }}
                >
                  {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AcceptAsDriver({
  request,
  drivers,
  onAccepted,
}: {
  request: DemoStatus["openDeliveries"][number];
  drivers: DemoStatus["demoDrivers"];
  onAccepted: () => void;
}) {
  const { toast } = useToast();
  const [driverId, setDriverId] = useState<string>(drivers[0]?.id ?? "");
  const [dayOffset, setDayOffset] = useState(0);
  const [hour, setHour] = useState(14);

  const accept = useMutation({
    mutationFn: async () => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + dayOffset);
      d.setHours(hour, 0, 0, 0);
      const res = await apiRequest(
        "POST",
        `/api/admin/demo-control/deliveries/${request.id}/accept-as-driver`,
        { driverId, scheduledFor: d.toISOString() },
      );
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Accepted as demo driver" });
      onAccepted();
    },
    onError: (err: Error) => toast({ title: "Failed", description: err.message, variant: "destructive" }),
  });

  return (
    <div className="rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-1">
        <span className="font-medium">
          {request.buyerCity}, {request.buyerState} — {request.stops} stop{request.stops === 1 ? "" : "s"}
        </span>
        <Badge variant="outline">${Number(request.driverPay).toFixed(2)}</Badge>
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <div>
          <Label className="text-xs">Driver</Label>
          <Select value={driverId} onValueChange={setDriverId}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Pick driver" />
            </SelectTrigger>
            <SelectContent>
              {drivers.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Day</Label>
          <Select value={String(dayOffset)} onValueChange={(v) => setDayOffset(Number(v))}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 7 }, (_, i) => {
                const d = new Date();
                d.setDate(d.getDate() + i);
                return (
                  <SelectItem key={i} value={String(i)}>
                    {i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Window</Label>
          <Select value={String(hour)} onValueChange={(v) => setHour(Number(v))}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">Morning</SelectItem>
              <SelectItem value="14">Afternoon</SelectItem>
              <SelectItem value="18">Evening</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button
        size="sm"
        className="mt-2"
        disabled={!driverId || accept.isPending}
        onClick={() => accept.mutate()}
      >
        {accept.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Accept as this driver
      </Button>
    </div>
  );
}

function BuyAsDemoBuyer({
  listing,
  buyers,
  onPlaced,
}: {
  listing: DemoStatus["betaListings"][number];
  buyers: DemoStatus["demoBuyers"];
  onPlaced: () => void;
}) {
  const { toast } = useToast();
  const [buyerId, setBuyerId] = useState<string>(buyers[0]?.id ?? "");
  const [qty, setQty] = useState<number>(1);

  const place = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/demo-control/buy-as-demo-buyer", {
        buyerId,
        itemId: listing.id,
        qty,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Order placed as demo buyer" });
      onPlaced();
    },
    onError: (err: Error) =>
      toast({ title: "Failed", description: err.message, variant: "destructive" }),
  });

  return (
    <div className="rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-1">
        <span className="font-medium">{listing.name}</span>
        <Badge variant="outline">${Number(listing.price).toFixed(2)}</Badge>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Seller: {listing.sellerDisplayName ?? "(unnamed tester)"} · stock: {listing.qtyAvailable}
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <div>
          <Label className="text-xs">Buyer</Label>
          <Select value={buyerId} onValueChange={setBuyerId}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Pick demo buyer" />
            </SelectTrigger>
            <SelectContent>
              {buyers.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Qty</Label>
          <Select value={String(qty)} onValueChange={(v) => setQty(Number(v))}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button
        size="sm"
        className="mt-2"
        disabled={!buyerId || listing.qtyAvailable < 1 || place.isPending}
        onClick={() => place.mutate()}
      >
        {place.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Place order as this buyer
      </Button>
    </div>
  );
}

